// #region imports

// --- Angular
import { Injectable, PLATFORM_ID, Inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

// --- RxJs
import { Observable, from, BehaviorSubject, of } from 'rxjs';
import { filter, take, tap, map } from 'rxjs/operators';

// --- 3rd Party
import {
  UserManager,
  OidcClient,
  Log,
  User as OidcUser,
  SigninRequest,
  SignoutRequest,
  UserManagerSettings
} from 'oidc-client';

// --- HS Code
import { AppService } from '../app.service';
import { environment } from '../../../../environments/environment';
import { OidcEvents, StorageKeys } from '../../../models';

// #endregion

@Injectable({
  providedIn: 'root'
})
export class OidcService {
  private _isLoading$: BehaviorSubject<boolean>;
  private _identity$: BehaviorSubject<OidcUser>;

  private _oidcUserManager: UserManager;
  private _oidcClient: OidcClient;

  private _useCallbackFlag = true;

  private _oidcSettings: UserManagerSettings;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private appService: AppService
  ) {
    this._isLoading$ = new BehaviorSubject<boolean>(false);

    const logSettings = environment.authSettings.authLogger;
    const clientSettings = environment.authSettings;

    if (environment.useCallbackFlag != null) {
      this._useCallbackFlag = environment.useCallbackFlag;
    } else {
      this._useCallbackFlag = true;
    }

    if (logSettings) {
      Log.level = logSettings.level;
      Log.logger = logSettings.logger;
    }

    this._oidcSettings = {
      authority: clientSettings.authority,
      client_id: clientSettings.client_id,
      response_type: clientSettings.response_type,
      scope: clientSettings.scope,
      redirect_uri: clientSettings.redirect_uri,
      post_logout_redirect_uri: clientSettings.post_logout_redirect_uri,
      silent_redirect_uri: clientSettings.silent_redirect_uri,
      automaticSilentRenew: clientSettings.automaticSilentRenew
    };

    this._oidcUserManager = new UserManager(this._oidcSettings);
    this._oidcClient = new OidcClient(this._oidcSettings);

    this._isLoading$ = new BehaviorSubject<boolean>(false);
    this._identity$ = new BehaviorSubject<OidcUser>(null);
  }

  get identity$(): Observable<OidcUser> {
    return from(this._oidcUserManager.getUser());
  }

  getUserManager(): UserManager {
    return this._oidcUserManager;
  }

  getOidcClient(): OidcClient {
    return this._oidcClient;
  }

  waitForAuthenticationLoaded(): Observable<boolean> {
    return this._isLoading$.pipe(
      filter(loading => loading === false),
      take(1)
    );
  }

  getOidcUser(): void {
    this._isLoading$.next(true);
    from(this._oidcUserManager.getUser()).subscribe((oidcUser: OidcUser) => {
      this._isLoading$.next(false);
      const automaticSilentRenew =
        this._oidcSettings.automaticSilentRenew != null &&
        this._oidcSettings.automaticSilentRenew;
      if (
        oidcUser != null &&
        oidcUser.expired === true &&
        automaticSilentRenew === true
      ) {
        this.signInSilent();
      } else {
        this._identity$.next(oidcUser);
      }
    });
  }

  removeOidcUser(): Observable<void> {
    return from(this._oidcUserManager.removeUser());
  }

  registerOidcEvent(event: OidcEvents, callback: (...ev: any[]) => void) {
    switch (event) {
      case OidcEvents.AccessTokenExpired:
        this._oidcUserManager.events.addAccessTokenExpired(callback);
        break;
      case OidcEvents.AccessTokenExpiring:
        this._oidcUserManager.events.addAccessTokenExpiring(callback);
        break;
      case OidcEvents.SilentRenewError:
        this._oidcUserManager.events.addSilentRenewError(callback);
        break;
      case OidcEvents.UserLoaded:
        this._oidcUserManager.events.addUserLoaded(callback);
        break;
      case OidcEvents.UserSessionChanged:
        this._oidcUserManager.events.addUserSessionChanged(callback);
        break;
      case OidcEvents.UserSignedOut:
        this._oidcUserManager.events.addUserSignedOut(callback);
        break;
      case OidcEvents.UserUnloaded:
        this._oidcUserManager.events.addUserUnloaded(callback);
        break;
      default:
        break;
    }
  }

  removeOidcEvent(event: OidcEvents, callback: (...ev: any[]) => void) {
    switch (event) {
      case OidcEvents.AccessTokenExpired:
        this._oidcUserManager.events.removeAccessTokenExpired(callback);
        break;
      case OidcEvents.AccessTokenExpiring:
        this._oidcUserManager.events.removeAccessTokenExpiring(callback);
        break;
      case OidcEvents.SilentRenewError:
        this._oidcUserManager.events.removeSilentRenewError(callback);
        break;
      case OidcEvents.UserLoaded:
        this._oidcUserManager.events.removeUserLoaded(callback);
        break;
      case OidcEvents.UserSessionChanged:
        this._oidcUserManager.events.removeUserSessionChanged(callback);
        break;
      case OidcEvents.UserSignedOut:
        this._oidcUserManager.events.removeUserSignedOut(callback);
        break;
      case OidcEvents.UserUnloaded:
        this._oidcUserManager.events.removeUserUnloaded(callback);
        break;
      default:
        break;
    }
  }

  signInPopup(args?: any): Observable<OidcUser> {
    this.setCallbackInformation(true);

    return from(this._oidcUserManager.signinPopup({ ...args }));
  }

  signInRedirect(args?: any): Observable<void> {
    this.setCallbackInformation(false);

    return from(this._oidcUserManager.signinRedirect({ ...args }));
  }

  signOutPopup(args?: any): Observable<any> {
    this.setCallbackInformation(true);

    return from(this._oidcUserManager.signoutPopup({ ...args }));
  }

  signOutRedirect(args?: any): Observable<any> {
    this.setCallbackInformation(false);

    this._oidcUserManager.removeUser().catch((reason) => {  
      console.error(reason);
    });
    const user = this._identity$.value;
    return from(this._oidcUserManager.signoutRedirect({ id_token_hint: user  && user.id_token }));
  }

  signInSilent(args?: any): void {
    from(this._oidcUserManager.signinSilent({ ...args })).subscribe(
      (oidcUser: OidcUser) => {
        this._identity$.next(oidcUser);
      }
    );
  }

  signinPopupCallback(): Observable<any> {
    return from(this._oidcUserManager.signinPopupCallback());
  }

  signinRedirectCallback(): Observable<OidcUser> {
    return from(this._oidcUserManager.signinRedirectCallback());
  }

  signoutPopupCallback(): Observable<void> {
    return from(this._oidcUserManager.signoutPopupCallback());
  }

  signoutRedirectCallback(): Observable<any> {
    return from(this._oidcUserManager.signoutRedirectCallback());
  }

  getSigninUrl(args?: any): Observable<SigninRequest> {
    return from(this._oidcUserManager.createSigninRequest(args));
  }

  getSignoutUrl(args?: any): Observable<SignoutRequest> {
    return from(this._oidcUserManager.createSignoutRequest(args));
  }

  private setCallbackInformation(isPopupCallback: boolean) {
    // is browser and useCallbackFlag set to true or defaults to true
    if (isPlatformBrowser(this.platformId) && this._useCallbackFlag) {
      localStorage.setItem(StorageKeys.PopupCallback, `${isPopupCallback}`);
    }
  }
}
